import type { AssertByTypename, WithNonNullable, WithRequired } from '@/helpers/types';
import type { GetTopArticlesQuery, TopArticleFragment } from '@/pages/graphql/queries/GetTopArticles.query';
import type {
	GetTopTitlesQuery,
	TopTitle_Movie_Fragment,
	TopTitle_Season_Fragment,
	TopTitle_Show_Fragment,
} from '@/pages/graphql/queries/GetTopTitles.query';

import { GetTopArticlesDocument } from '@/pages/graphql/queries/GetTopArticles.query';
import { GetTopTitlesDocument } from '@/pages/graphql/queries/GetTopTitles.query';

import { useUserStore } from '@/stores/pinia/useUserStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

import { headerfulCachedRequest } from '@/helpers/request-helper';

import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

export const useFooterStore = defineStore('footer', () => {
	const routingStore = useRoutingStore();
	const languageStore = useLanguageStore();
	const userStore = useUserStore();

	const topMovies = ref<TopTitle_Movie_Fragment[]>([]);
	const topUpcomingMovies = ref<TopTitle_Movie_Fragment[]>([]);
	const topShows = ref<TopTitle_Show_Fragment[]>([]);
	const topUpcomingShows = ref<TopTitle_Season_Fragment[]>([]);

	type TopArticleWithTitle = Omit<TopArticleFragment, 'content'> & {
		content: WithNonNullable<WithRequired<TopArticleFragment['content'], 'title'>, 'title'>;
	};

	const topArticles = ref<TopArticleWithTitle[]>([]);

	async function fetchTopTitles() {
		const variables = {
			country: languageStore.country,
			language: languageStore.language,
		};

		const response = await headerfulCachedRequest(GetTopTitlesDocument, variables, userStore.jwId);
		const data: GetTopTitlesQuery | null = response?.data ?? null;

		topMovies.value = data?.movie?.edges?.map(({ node }) => node as AssertByTypename<typeof node, 'Movie'>) ?? [];

		topUpcomingMovies.value =
			data?.upcomingMovie?.edges?.map(({ node }) => node as AssertByTypename<typeof node, 'Movie'>) ?? [];

		topShows.value = data?.show?.edges?.map(({ node }) => node as AssertByTypename<typeof node, 'Show'>) ?? [];

		topUpcomingShows.value =
			data?.upcomingShow?.edges?.map(({ node }) => node as AssertByTypename<typeof node, 'Season'>) ?? [];
	}

	async function fetchTopArticles() {
		const variables = {
			country: languageStore.country,
			language: languageStore.language,
			site: routingStore.activeDomain,
			first: 5,
		};

		const response = await headerfulCachedRequest(GetTopArticlesDocument, variables, userStore.jwId);
		const data: GetTopArticlesQuery | null = response?.data ?? null;

		topArticles.value = (data?.articles.edges ?? [])
			.map(({ node }) => node)
			.filter((article): article is TopArticleWithTitle => article.content.title != null);
	}

	watch(
		() => languageStore.webLocale,
		(webLocale, oldWebLocale) => {
			if (webLocale === oldWebLocale) return;
			if (import.meta.server) return;

			fetchTopTitles();
			fetchTopArticles();
		}
	);

	return {
		topMovies,
		topUpcomingMovies,
		topShows,
		topUpcomingShows,
		fetchTopTitles,
		topArticles,
		fetchTopArticles,
	};
});
